import React, { useState, useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import { Form, Image, Alert, Card, Spinner, Button, Modal } from "react-bootstrap";
import UploadFile from "./UploadFile";
import { getSignedUrl } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import axios from "axios";

const QuizImageUpload = forwardRef(
  ({ onImageUpload, onPendingFilesChange, existingImages, effectiveCourseId, activeSection, editQuiz }, ref) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [descriptions, setDescriptions] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState("");

    // Notify parent if there are unuploaded files pending
    useEffect(() => {
      if (onPendingFilesChange) {
        onPendingFilesChange(selectedFiles.length > 0);
      }
    }, [selectedFiles, onPendingFilesChange]);
    const [cookies] = useCookies(["userId"]);
    const [deletingFiles, setDeletingFiles] = useState({});
    const [queuedDeletions, setQueuedDeletions] = useState([]);
    const [fullscreenImage, setFullscreenImage] = useState(null);

    // Initialize existing images in edit mode
    useEffect(() => {
      const initializeImages = async () => {
        if (!editQuiz || !existingImages?.length) {
          setPreviews([]);
          setDescriptions({});
          setQueuedDeletions([]);
          if (!editQuiz) {
            onImageUpload([]);
          }
          return;
        }

        const existingImageUrls = existingImages
          .map(img => (typeof img === "string" ? img.split("?")[0] : img.url?.split("?")[0] || ""))
          .filter(url => url);
        const previewUrls = previews.map(preview => preview.url?.split("?")[0]).filter(url => url);
        if (
          existingImageUrls.length === previewUrls.length &&
          existingImageUrls.every((url, i) => url === previewUrls[i])
        ) {
          return;
        }

        const initialPreviews = await Promise.all(
          existingImages.map(async (img, index) => {
            const isString = typeof img === "string";
            const fileName = isString ? img.split("?")[0].split("/").pop() : img.url?.split("?")[0].split("/").pop() || `image-${index}`;
            let url = isString ? img : img.url;
            const description = isString ? "" : img.description || "";

            if (!url?.startsWith("blob:")) {
              try {
                const payload = {
                  courseId: effectiveCourseId,
                  sectionId: activeSection?.sectionId,
                  fileName,
                  method: "GET",
                  userId: cookies.userId,
                };
                const response = await getSignedUrl(payload);
                url = response.data.preSignedUrl;
              } catch (error) {
                url = null;
              }
            }

            return {
              url,
              fileName,
              description,
              order: index,
              file: isString ? null : img.file || null,
            };
          })
        );

        const validPreviews = initialPreviews.filter(preview => preview.url);
        setPreviews(validPreviews);
        setDescriptions(
          Object.fromEntries(
            validPreviews.map(preview => [preview.fileName, preview.description])
          )
        );
        onImageUpload(validPreviews.map(preview => ({
          file: preview.file,
          url: preview.url?.split("?")[0],
          description: preview.description,
        })));
      };

      initializeImages();

      return () => {
        previews.forEach(preview => {
          if (preview.url?.startsWith("blob:")) {
            URL.revokeObjectURL(preview.url);
          }
        });
      };
    }, [editQuiz, existingImages, effectiveCourseId, activeSection, cookies.userId]);

    // Sanitize file name by replacing spaces with underscores
    const sanitizeFileName = (fileName) => {
      return fileName.replace(/\s+/g, '_');
    };

    // Handle file selection with space validation
    const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) {
        setError("Please select at least one image");
        setSelectedFiles([]);
        onImageUpload(editQuiz ? previews.map(preview => ({
          file: preview.file,
          url: preview.url?.split("?")[0],
          description: descriptions[preview.fileName] || "",
        })) : []);
        return;
      }

      // Clean and sanitize file names to strict S3 safe formats
      const renamedFiles = files.map(file => {
        let cleanName = file.name.toLowerCase();
        // Replace any character that is NOT a lowercase letter, number, dot, hyphen, or underscore with an underscore "_"
        cleanName = cleanName.replace(/[^a-z0-9.\-_]/g, '_');
        
        // Prevent consecutive multiple underscores for cleanliness
        cleanName = cleanName.replace(/_+/g, '_');

        if (cleanName !== file.name) {
          return new File([file], cleanName, { type: file.type });
        }
        return file;
      });

      setError("");
      setSelectedFiles(renamedFiles);

      if (!editQuiz) {
        setPreviews(renamedFiles.map((file, index) => ({
          fileName: file.name,
          url: null,
          description: "",
          order: index,
          file,
        })));
        onImageUpload(renamedFiles.map(file => ({ file, url: null, description: "" })));
      }
    };

    // Immediately Delete image from S3 and remove from UI
    const handleDeleteImage = async (fileName, index) => {
      if (!editQuiz) return;
      
      setIsUploading(true);
      setError("");

      try {
        const sanitizedFileName = sanitizeFileName(fileName);
        const payload = {
          courseId: effectiveCourseId,
          sectionId: activeSection?.sectionId,
          fileName: sanitizedFileName,
          method: "DELETE",
          userId: cookies.userId,
        };

        const signedUrlResponse = await getSignedUrl(payload);

        if (signedUrlResponse.data?.message !== "Object deleted successfully") {
          if (!signedUrlResponse.data?.preSignedUrl) {
            throw new Error(`Failed to get delete URL for ${sanitizedFileName}`);
          }

          await axios.delete(signedUrlResponse.data.preSignedUrl, {
            validateStatus: status => status >= 200 && status < 300 || status === 204,
          });
        }

        // Object successfully deleted, remove from state
        setPreviews(prev => {
          const newPreviews = prev.filter((_, i) => i !== index);
          onImageUpload(
            newPreviews.map(preview => ({
              file: preview.file,
              url: preview.url?.split("?")[0],
              description: descriptions[preview.fileName] || "",
            }))
          );
          return newPreviews;
        });
        
        setDescriptions(prev => {
          const newDescriptions = { ...prev };
          delete newDescriptions[fileName];
          return newDescriptions;
        });
        
        setQueuedDeletions(prev => prev.filter(q => q.fileName !== fileName));

      } catch (error) {
        console.error(`Error deleting image:`, error);
        setError("Failed to delete image. Please try again.");
      } finally {
        setIsUploading(false);
      }
    };

    // Expose methods for AddQuizDialog
    useImperativeHandle(ref, () => ({
      uploadImages: async () => {
        if (!selectedFiles.length && !editQuiz) {
          return previews.map(preview => ({
            file: preview.file,
            url: preview.url?.split("?")[0],
            description: descriptions[preview.fileName] || "",
          }));
        }

        if (!effectiveCourseId || !activeSection?.sectionId || !cookies?.userId) {
          setError("Missing required information. Please check course, section, and login status.");
          return editQuiz ? previews.map(preview => ({
            file: preview.file,
            url: preview.url?.split("?")[0],
            description: descriptions[preview.fileName] || "",
          })) : [];
        }

        setIsUploading(true);
        setError("");

        try {
          const uploadResults = await Promise.all(
            selectedFiles.map(async (file) => {
              const sanitizedFileName = sanitizeFileName(file.name);
              const payload = {
                courseId: effectiveCourseId,
                sectionId: activeSection.sectionId,
                fileName: sanitizedFileName,
                method: "PUT",
                userId: cookies.userId,
              };

              const signedUrlResponse = await getSignedUrl(payload);
              if (!signedUrlResponse.data?.preSignedUrl) {
                throw new Error(`Failed to get upload URL for ${sanitizedFileName}`);
              }

              await axios.put(signedUrlResponse.data.preSignedUrl, file, {
                headers: { "Content-Type": file.type },
              });

              return {
                file,
                url: signedUrlResponse.data.objectUrl.split("?")[0].split("/").pop(),
                description: descriptions[file.name] || "",
              };
            })
          );

          if (editQuiz) {
            setPreviews(prev => [
              ...prev,
              ...uploadResults.map((result, index) => ({
                url: result.url,
                fileName: result.file.name,
                description: result.description,
                order: prev.length + index,
                file: result.file,
              })),
            ]);
          }
          setSelectedFiles([]);
          const updatedImages = [
            ...previews.map(preview => ({
              file: preview.file,
              url: preview.url?.split("?")[0],
              description: descriptions[preview.fileName] || "",
            })),
            ...uploadResults,
          ];
          onImageUpload(updatedImages);
          return updatedImages;
        } catch (err) {
          console.error("Upload error:", err);
          setError("Failed to upload images. Please try again.");
          return editQuiz ? previews.map(preview => ({
            file: preview.file,
            url: preview.url?.split("?")[0],
            description: descriptions[preview.fileName] || "",
          })) : [];
        } finally {
          setIsUploading(false);
        }
      },
      processDeletions: async () => {
        if (!queuedDeletions.length) return;

        setIsUploading(true);
        setError("");

        try {
          await Promise.all(
            queuedDeletions.map(async ({ fileName }) => {
              const sanitizedFileName = sanitizeFileName(fileName);
              const payload = {
                courseId: effectiveCourseId,
                sectionId: activeSection.sectionId,
                fileName: sanitizedFileName,
                method: "DELETE",
                userId: cookies.userId,
              };

              const signedUrlResponse = await getSignedUrl(payload);

              if (signedUrlResponse.data?.message === "Object deleted successfully") {
                return;
              }

              if (!signedUrlResponse.data?.preSignedUrl) {
                throw new Error(`Failed to get delete URL for ${sanitizedFileName}`);
              }

              await axios.delete(signedUrlResponse.data.preSignedUrl, {
                validateStatus: status => status >= 200 && status < 300 || status === 204,
              });
            })
          );
          setQueuedDeletions([]);
        } catch (error) {
          console.error(`Error deleting images:`, error);
          setError("Failed to delete some images. Please try again.");
        } finally {
          setIsUploading(false);
        }
      },
    }));

    // Handle description changes for image options (edit mode only)
    const handleDescriptionChange = (fileName, value) => {
      if (!editQuiz) return;
      setDescriptions(prev => ({ ...prev, [fileName]: value }));
      onImageUpload(
        previews.map(preview => ({
          file: preview.file,
          url: preview.url?.split("?")[0],
          description: preview.fileName === fileName ? value : descriptions[preview.fileName] || "",
        }))
      );
    };

    return (
      <div className="p-4 bg-light rounded border">
        <div className="mb-4">
          <h5 className="fw-semibold text-dark mb-2">
            <i className="bi bi-images me-2"></i>
            Quiz Images
          </h5>
          <p className="text-muted small mb-0">
            {editQuiz
              ? "Preview or queue images for deletion. Upload new images on submit."
              : "Select images to upload on submit. View selected file names."}
          </p>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Form.Group>
              <Form.Label className="fw-medium mb-3">Select Images</Form.Label>
              <UploadFile
                fileType="image"
                handleFileChange={handleFileChange}
                customText="Choose Images"
                showFileInfo={true}
                multiple={true}
                disabled={isUploading}
              />
              <Form.Text className="text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Supported: JPG & PNG. Multiple selection available. File names must not contain spaces.
              </Form.Text>
            </Form.Group>

            {selectedFiles.length > 0 && (
              <div className="mt-3">
                <p className="small text-muted mb-1">Selected Files:</p>
                <ul className="list-unstyled small">
                  {selectedFiles.map((file, index) => (
                    <li key={index} className="text-dark">{file.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mt-3 mb-0 small">
                {error}
              </Alert>
            )}
            
            {isUploading && (
              <Alert variant="info" className="mt-3 mb-0 small">
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Processing images...
              </Alert>
            )}
          </Card.Body>
        </Card>

        {editQuiz && previews.length > 0 && (
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-medium mb-0">Image Preview</h6>
                <span className="badge bg-primary">
                  {previews.length} {previews.length === 1 ? 'Image' : 'Images'}
                </span>
              </div>

              <div className="space-y-3">
                {previews.map((preview, index) => (
                  <div key={index} className="d-flex gap-3 p-3 border rounded bg-white">
                    <div className="position-relative flex-shrink-0">
                      <div className="position-absolute top-0 start-0 translate-middle badge bg-primary border border-2 border-white">
                        {index + 1}
                      </div>
                      {preview.url || preview.file ? (
                        <Image
                          src={
                            preview.url?.startsWith("http") || preview.url?.startsWith("blob:") 
                              ? preview.url 
                              : (preview.file ? URL.createObjectURL(preview.file) : preview.url)
                          }
                          alt={`Preview ${index + 1}`}
                          className="rounded border"
                          style={{ 
                            width: '100px', 
                            height: '100px', 
                            objectFit: 'cover',
                            cursor: 'pointer'
                          }}
                          onClick={() => setFullscreenImage(preview)}
                        />
                      ) : (
                        <div
                          style={{
                            width: '100px',
                            height: '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f8f9fa',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                        >
                          <span style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                            No image
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-grow-1 min-w-0">
                      <div className="text-muted small font-monospace mb-2 text-truncate">
                        {preview.fileName}
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDeleteImage(preview.fileName, index)}
                        className="px-3"
                        disabled={isUploading}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {editQuiz && previews.length === 0 && !error && !isUploading && (
          <Card className="text-center py-5">
            <Card.Body>
              <i className="bi bi-image display-4 text-muted mb-3"></i>
              <h6 className="text-muted mb-2">No Images Uploaded</h6>
              <p className="text-muted small mb-0">
                Select images using the upload button above
              </p>
            </Card.Body>
          </Card>
        )}

        <Modal show={!!fullscreenImage} onHide={() => setFullscreenImage(null)} fullscreen>
          <Modal.Header closeButton>
            <Modal.Title className="text-truncate">{fullscreenImage?.fileName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex justify-content-center align-items-center bg-dark p-0">
            <Image 
              src={fullscreenImage ? (fullscreenImage.url?.startsWith("http") || fullscreenImage.url?.startsWith("blob:") ? fullscreenImage.url : (fullscreenImage.file ? URL.createObjectURL(fullscreenImage.file) : fullscreenImage.url)) : ""} 
              alt={fullscreenImage?.fileName} 
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} 
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }
);

export default QuizImageUpload;